import { ErrorHandler, Injectable } from '@angular/core';

/** sessionStorage key holding the time of the last reload this module triggered. */
const RELOAD_KEY = 'chunk-load-error-reload-at';

/** A second chunk error within this window is not answered with another reload. */
const RELOAD_GUARD_MS = 10_000;

/**
 * Messages browsers use when a lazy chunk cannot be loaded: Chrome, Firefox, Safari, plus the
 * webpack wording in case a library still brings it along.
 */
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i
];

/**
 * Whether an error means a lazy chunk is gone. After a deploy, a tab that still runs the old
 * `main-*.js` asks for chunks the deploy deleted (SHIP-1903). Unwraps the Angular and promise
 * wrappers the error may arrive in.
 */
export function isChunkLoadError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; current && depth < 5; depth++) {
    const candidate = current as { name?: unknown; message?: unknown; rejection?: unknown; error?: unknown };
    const text = `${candidate.name ?? ''} ${candidate.message ?? (typeof current === 'string' ? current : '')}`;
    if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))) return true;
    current = candidate.rejection ?? candidate.error;
  }
  return false;
}

/**
 * Loads the app once more from the server, so the tab picks up the current `index.html` and with
 * it the current chunks. Guarded by sessionStorage: if the chunk is still missing right after the
 * reload, the error stays visible instead of reloading forever.
 *
 * @param url Where to go; the URL of the failed navigation, so the user lands where they wanted.
 * @returns whether a reload was started
 */
export function reloadOnceAfterChunkError(url?: string): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY));
    if (lastReload && Date.now() - lastReload < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage there is no loop guard, so rather no reload at all.
    return false;
  }

  if (url) window.location.assign(url);
  else window.location.reload();
  return true;
}

/** Reloads once on a chunk load error, hands everything else to Angular's default handling. */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    if (isChunkLoadError(error) && reloadOnceAfterChunkError()) return;
    super.handleError(error);
  }
}
